import { useP } from '../i18n/useP';
import PageShell from '../components/PageShell';
import './TenantSettingsPage.css';
import './DesignPage.css';

/* Résout un token `--color-*` en son hex CALCULÉ (getComputedStyle) au lieu
   d'un hex recopié à la main : la carte ne peut plus dériver de index.css
   (constat 2026-08-15 — 4 des 7 couleurs de surface affichaient une valeur
   périmée). Lu une fois au montage ; les tokens ne changent qu'au reload. */
const cssVar = (name) => (
  typeof window === 'undefined' ? ''
    : getComputedStyle(document.documentElement).getPropertyValue(name).trim().toUpperCase()
);

/**
 * Page Design — charte graphique (référence couleurs + typographie).
 * Apparence standard pour tous les clients : un seul accent (ardoise) défini par
 * la charte (`--color-*` dans index.css). Plus de panachage par client.
 */
export default function DesignPage({ embedded = false }) {
  const p = useP();
  const content = (
      <section className="settings-card">
        <h2 className="settings-card-title">{p('Charte graphique')}</h2>
        <p className="settings-card-desc">
          {p("Principe directeur : toute couleur nomme son rôle en un mot — ardoise = accent, sage = favorable, terracotta = alerte, bleu = saisie. Une couleur sans rôle n’entre pas dans la charte.")}
        </p>

        <div className="ds-sub">{p('Surfaces — échelle d’élévation')}</div>
        <div className="ds-swatches">
          {[
            ['Canvas (fond, derrière les panneaux)', '--color-canvas'],
            ['Surface page (zone contenu)', '--color-surface-page'],
            ['Surface rail (navigation)', '--color-surface-rail'],
            ['Surface card (blanc)', '--color-surface-card'],
            ['Bordure (filet général)', '--color-border'],
            ['Bordure interne (tuiles de card)', '--color-border-inner'],
            ['Filet rail ↔ contenu', '--color-border-rail'],
          ].map(([name, v]) => (
            <div className="ds-swatch" key={v}>
              <span className="ds-chip" style={{ background: `var(${v})` }} />
              <span className="ds-name">{p(name)}</span>
              <span className="ds-hex">{cssVar(v)}</span>
            </div>
          ))}
        </div>

        <div className="ds-sub">{p('Accent — ardoise (seul accent du chrome)')}</div>
        <div className="ds-swatches">
          {[
            ['Accent (nav active, onglet, liserés, graphe réel)', '--color-accent'],
            ['Accent clair (prévisionnel / extrapolé)', '--color-accent-soft'],
            ['Accent tint (fond figure-clé)', '--color-accent-tint'],
            ['Texte sur tint', '--color-on-tint'],
          ].map(([name, v]) => (
            <div className="ds-swatch" key={v}>
              <span className="ds-chip" style={{ background: `var(${v})` }} />
              <span className="ds-name">{p(name)}</span>
              <span className="ds-hex">{cssVar(v)}</span>
            </div>
          ))}
        </div>

        <div className="ds-sub">{p('Variations chiffrées & rôles sémantiques')}</div>
        <div className="ds-swatches">
          {[
            ['Delta neutre (défaut · + flèche)', '--color-delta-neutral'],
            ['Delta favorable (sage)', '--color-delta-favorable'],
            ['Delta alerte (terracotta · rare)', '--color-delta-alert'],
            ['Navy (primaire · titres, KPI, totaux)', '--color-navy'],
            ['Amber (secondaire · ponctuel)', '--color-amber'],
            ['Bleu saisie (champs de formulaire uniquement)', '--color-field-focus'],
            ['Texte', '--color-text'],
            ['Texte atténué', '--color-text-muted'],
          ].map(([name, v]) => (
            <div className="ds-swatch" key={v}>
              <span className="ds-chip" style={{ background: `var(${v})` }} />
              <span className="ds-name">{p(name)}</span>
              <span className="ds-hex">{cssVar(v)}</span>
            </div>
          ))}
        </div>

        <div className="ds-sub">{p('Typographie — 2 voix')}</div>
        <div className="ds-fonts">
          <div className="ds-font">
            <div className="ds-font-sample ds-font-title">{p('Aa — Titres')}</div>
            <div className="ds-font-meta"><b>Schibsted Grotesk</b>, Inter, sans-serif · {p('titres de page, en-têtes')}</div>
          </div>
          <div className="ds-font">
            <div className="ds-font-sample ds-font-body">{p('Aa — Contenu')}</div>
            <div className="ds-font-meta"><b>Inter</b>, system-ui, sans-serif · {p('texte, tables, formulaires')}</div>
          </div>
        </div>
      </section>
  );

  return embedded ? content : <PageShell framed timeless title="Design">{content}</PageShell>;
}
